/**
 * useManageGroup Hook
 * Handles core group management operations (create, update, edit, duplicate)
 * Split from useManageGroupOperations.ts for better separation of concerns
 */

import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { serviceFactory } from '@/services/core/serviceFactory';
import { GroupsService } from '@/services/features/GroupsService';
import { GroupFilteringService } from '@/services/features/GroupFilteringService';
import type { CreateGroupState, CreateGroupStateActions } from './useManageGroupState';

// Define a local clinic interface for useManageGroup
interface ManageGroupClinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
}
export interface GroupData {
  name: string;
  type: number;
  status: number;
  clinic_id: number;
  enrollment_specialist_id: number[];
  patient_ids: number[];
  group_filters?: any;
}

export interface ManageGroupOperations {
  // Data loading operations
  loadInitialData: () => Promise<void>;
  loadGroupDetailsForEdit: (groupId: number) => Promise<void>;
  loadGroupDetailsForDuplicate: (groupId: number) => Promise<void>;
  loadGroupPatientList: (groupId: number) => Promise<void>;
  
  // Group operations
  handleCreateGroup: () => Promise<void>;
  handleUpdateGroup: () => Promise<void>;
  handleCreateDuplicateGroup: () => Promise<void>;
  
  // Validation operations
  checkGroupNameExists: (groupName: string) => Promise<boolean>;
  validateForm: () => { isValid: boolean; errors: string[] };
  
  // Patient selection operations
  handlePatientSelection: (patientId: number, checked: boolean) => void;
  handleSelectAll: (checked: boolean) => void;
  handleEmrPatientSelection: (patientId: number, checked: boolean) => void;
  handleEmrSelectAll: (checked: boolean) => void;
  
  // Utility operations
  resetForm: () => void;
}

export const useManageGroup = (
  state: CreateGroupState,
  actions: CreateGroupStateActions,
  onSuccess?: () => void
): ManageGroupOperations => {
  const { toast } = useToast();
  const { user, hasuraToken } = useAuth();
  const groupsService = serviceFactory.getService(GroupsService);
  const filteringService = serviceFactory.getService(GroupFilteringService);

  // Initialize service context
  if (user?.id && hasuraToken) {
    groupsService.updateContext({
      userId: user.id,
      hasuraToken,
      userType: user.type
    });
    filteringService.updateContext({
      userId: user.id,
      hasuraToken,
      userType: user.type
    });
  }

  /**
   * Load initial data (group types and clinics)
   */
  const loadInitialData = useCallback(async () => {
    actions.setIsLoading(true);
    try {
      // Only load group types in initial data, clinics will be loaded by filtering service
      const groupTypes = await groupsService.loadGroupTypes();
      actions.setGroupTypes(groupTypes);
      
      // Load clinics using filtering service
      const clinicsData = await filteringService.loadClinics();
      // Map clinic data to expected Clinic interface
      const mappedClinics = clinicsData.map((clinic: any) => ({
        id: clinic.clinic_id,
        name: clinic.clinic_name,
        isActive: true, // Assume active if returned by service
        clinic_id: clinic.clinic_id,
        clinic_name: clinic.clinic_name,
        patient_clinic_timezone: clinic.patient_clinic_timezone
      }));
      actions.setClinics(mappedClinics);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast({
        title: "Error",
        description: "Failed to load initial data",
        variant: "destructive",
      });
    } finally {
      actions.setIsLoading(false);
    }
  }, [actions, groupsService, toast]);

  /**
   * Load group details for editing
   */
  const loadGroupDetailsForEdit = useCallback(async (groupId: number) => {
    actions.setIsLoading(true);
    try {
      const groupDetails = await groupsService.loadGroupDetailsForEdit(groupId);
      if (groupDetails) {
        actions.setGroupDetails(groupDetails);
        actions.setGroupName(groupDetails.group_name);
        actions.setSelectedGroupType(groupDetails.group_type_id.toString());
        
        // Set VPE selection
        if (Array.isArray(groupDetails.es_id)) {
          actions.setSelectedVPEs(groupDetails.es_id);
        } else if (groupDetails.es_id) {
          actions.setSelectedVPEs([groupDetails.es_id]);
        }
        
        // Set clinic and load dependent data
        if ((groupDetails.group_filters as any)?.clinicID) {
          const clinicId = (groupDetails.group_filters as any).clinicID;
          actions.setSelectedClinic(clinicId.toString());
          
          // Find clinic name for display
          const clinic = state.clinics.find(c => c.id === clinicId);
          if (clinic) {
            actions.setClinicSearchTerm(clinic.name);
          }
          
          actions.setCreateType('filters');
        }
        
        actions.setOriginalPatientCount(groupDetails.number_of_patients || 0);
        actions.setFilteredPatientsCount(groupDetails.number_of_patients || 0);
        
        // Load patient list
        await loadGroupPatientList(groupId);
      }
    } catch (error) {
      console.error('Error loading group details for edit:', error);
      toast({
        title: "Error",
        description: "Failed to load group details",
        variant: "destructive",
      });
    } finally {
      actions.setIsLoading(false);
    }
  }, [actions, groupsService, state.clinics, toast]);

  /**
   * Load group details for duplication
   */
  const loadGroupDetailsForDuplicate = useCallback(async (groupId: number) => {
    actions.setIsLoading(true);
    
    try {
      // First load initial data (group types and clinics) for duplicate mode
      const groupTypes = await groupsService.loadGroupTypes();
      actions.setGroupTypes(groupTypes);
      
      // Load clinics using filtering service
      const clinicsData = await filteringService.loadClinics();
      // Map clinic data to standard Clinic interface
      const mappedClinics = clinicsData.map((clinic: any) => ({
        id: clinic.clinic_id,
        name: clinic.clinic_name,
        isActive: true,
        clinic_id: clinic.clinic_id,
        clinic_name: clinic.clinic_name,
        patient_clinic_timezone: clinic.patient_clinic_timezone
      }));
      actions.setClinics(mappedClinics);
      
      const groupDetails = await groupsService.loadGroupDetailsForEdit(groupId);
      if (groupDetails) {
        actions.setGroupDetails(groupDetails);
        
        // For duplication, keep the same group name (validation will catch duplicates)
        actions.setGroupName(groupDetails.group_name);
        actions.setSelectedGroupType(groupDetails.group_type_id.toString());
        
        // Set VPE selection from original group
        if (Array.isArray(groupDetails.es_id)) {
          actions.setSelectedVPEs(groupDetails.es_id);
        } else if (groupDetails.es_id) {
          actions.setSelectedVPEs([groupDetails.es_id]);
        }
        
        // Set clinic and load all dependent data and filters
        if ((groupDetails.group_filters as any)?.clinicID) {
          const clinicId = (groupDetails.group_filters as any).clinicID;
          const filters = groupDetails.group_filters as any;
          
          actions.setSelectedClinic(clinicId.toString());
          
          // Find clinic name for display using the loaded clinics
          const clinic = mappedClinics.find(c => c.clinic_id === clinicId);
          if (clinic) {
            actions.setClinicSearchTerm(clinic.clinic_name);
            actions.setTimeZone(clinic.patient_clinic_timezone);
          }
          
          actions.setCreateType('filters');
          
          // Show filters and patient list immediately for duplicate mode
          actions.setShowFilters(true);
          actions.setShowPatientList(true);
          
          // Load and set all filters from the source group
          try {
            // Always load facilities for the clinic first
            const facilities = await filteringService.loadFacilities(clinicId);
            actions.setFacilities(facilities);
            
            // Set selected facilities if they exist
            if (filters.facilityID && filters.facilityID.length > 0) {
              actions.setSelectedFacilities(filters.facilityID);
            }
            
            // Load providers based on facilities or all providers for clinic
            const providers = filters.facilityID && filters.facilityID.length > 0
              ? await filteringService.loadProvidersForFacilities(clinicId, filters.facilityID)
              : await filteringService.loadProviders(clinicId);
            actions.setProviders(providers);
            
            // Set selected providers if they exist
            if (filters.providerID && filters.providerID.length > 0) {
              actions.setSelectedProviders(filters.providerID);
            }
            
            // Always load filter options to make all filter fields visible
            const filterOptions = await filteringService.getFilterOptions(
              clinicId,
              filters.facilityID,
              filters.providerID
            );
            
            // Always set filter options (even if empty) to make fields visible
            actions.setPrimaryDxOptions(filterOptions.primaryDx);
            actions.setSecondaryDxOptions(filterOptions.secondaryDx);
            actions.setPrimaryInsuranceOptions(filterOptions.primaryInsurance);
            actions.setSecondaryInsuranceOptions(filterOptions.secondaryInsurance);
            actions.setInsuranceTypeOptions(filterOptions.insuranceType);
            
            // Set selected diagnosis filters
            if (filters.primaryDx && filters.primaryDx.length > 0) {
              actions.setSelectedPrimaryDx(filters.primaryDx);
            }
            if (filters.secondaryDx && filters.secondaryDx.length > 0) {
              actions.setSelectedSecondaryDx(filters.secondaryDx);
            }
            
            // Set selected insurance filters
            if (filters.primaryInsurance && filters.primaryInsurance.length > 0) {
              actions.setSelectedPrimaryInsurance(filters.primaryInsurance);
            }
            if (filters.secondaryInsurance && filters.secondaryInsurance.length > 0) {
              actions.setSelectedSecondaryInsurance(filters.secondaryInsurance);
            }
            if (filters.insuranceTypes && filters.insuranceTypes.length > 0) {
              actions.setSelectedInsuranceType(filters.insuranceTypes);
            }
            
            // Set program eligibility
            if (filters.programEligibility && filters.programEligibility.length > 0) {
              actions.setSelectedProgramEligibility(filters.programEligibility);
            }
            
            // Set last visit date
            if (filters.formattedDate) {
              actions.setLastVisitDate(filters.formattedDate);
            }
            
            // Set exclude assigned groups flag
            if (filters.excludeFlag !== undefined) {
              actions.setExcludeAssignedGroups(filters.excludeFlag);
            }
            
          } catch (error) {
            console.error('Error loading filters for duplication:', error);
            // Continue with basic duplication even if filter loading fails
            // Still show the UI elements
            actions.setShowFilters(true);
            actions.setShowPatientList(true);
          }
        }
        
        // Set patient count from original group (keep original count, don't apply new filters)
        actions.setOriginalPatientCount(groupDetails.number_of_patients || 0);
        actions.setFilteredPatientsCount(groupDetails.number_of_patients || 0);
        
        // Load patient list from original group and set the selected patients
        const { patientList, patients } = await groupsService.loadGroupPatientList(groupId);
        actions.setSelectedPatients(patientList);
        actions.setSelectedPatientsForGroup(patientList);
        
        // Set the actual patient objects for the table display
        actions.setPatients(patients as any);
        
        // Set duplicate mode
        actions.setIsDuplicateMode(true);
        
        // No toast here - just loading completed
      }
    } catch (error) {
      console.error('Error loading group details for duplication:', error);
      toast({
        title: "Error",
        description: "Failed to load group details for duplication",
        variant: "destructive",
      });
    } finally {
      actions.setIsLoading(false);
    }
  }, [actions, groupsService, state.clinics, toast]);

  /**
   * Load group patient list
   */
  const loadGroupPatientList = useCallback(async (groupId: number) => {
    try {
      const { patientList, patients } = await groupsService.loadGroupPatientList(groupId);
      actions.setSelectedPatientsForGroup(patientList);
      actions.setPatients(patients as any);
    } catch (error) {
      console.error('Error loading group patient list:', error);
      toast({
        title: "Error",
        description: "Failed to load group patient list",
        variant: "destructive",
      });
    }
  }, [actions, groupsService, toast]);

  /**
   * Check if group name exists
   */
  const checkGroupNameExists = useCallback(async (groupName: string): Promise<boolean> => {
    try {
      return await groupsService.checkGroupNameExists(groupName);
    } catch (error) {
      console.error('Error checking group name:', error);
      return false;
    }
  }, [groupsService]);

  /**
   * Validate form
   */
  const validateForm = useCallback((): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!state.groupName.trim()) {
      errors.push("Please enter a group name.");
    }

    if (!state.selectedGroupType) {
      errors.push("Please select a group type.");
    }

    if (state.createType !== 'emrs' && state.selectedVPEs.length === 0) {
      errors.push("Please select enrollment specialist.");
    }

    if (!state.isEditMode) {
      const patientsToCreate = state.createType === 'emrs' ? state.selectedEmrPatients : state.selectedPatientsForGroup;
      if (patientsToCreate.length === 0) {
        errors.push("Please select at least one patient for the group.");
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [state]);

  /**
   * Create new group
   */
  const handleCreateGroup = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      toast({
        title: "Validation Error",
        description: validation.errors.join(" "),
        variant: "destructive"
      });
      return;
    }

    // Check if group name exists
    const nameExists = await checkGroupNameExists(state.groupName);
    if (nameExists && !state.isEditMode) {
      toast({
        title: "Validation Error",
        description: "This group name already exists. Please use a different name to proceed.",
        variant: "destructive"
      });
      return;
    }

    const confirmMessage = state.isEditMode 
      ? `Are you sure you want to update this group?`
      : `Are you sure you want to create a group?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    actions.setIsLoading(true);
    try {
      const patientsToCreate = state.createType === 'emrs' ? state.selectedEmrPatients : state.selectedPatientsForGroup;
      const selectedClinicData = state.clinics.find(c => c.id.toString() === state.selectedClinic);
      
      const groupData: GroupData = {
        name: state.groupName,
        type: parseInt(state.selectedGroupType),
        status: 2,
        clinic_id: parseInt(state.selectedClinic),
        enrollment_specialist_id: [...new Set(state.selectedVPEs)], // Remove duplicates
        patient_ids: patientsToCreate,
        group_filters: {
          clinic: selectedClinicData?.name || "",
          clinicID: parseInt(state.selectedClinic),
          facilityID: state.selectedFacilities.length > 0 ? state.selectedFacilities : undefined,
          facilities: state.selectedFacilities.length > 0 ?
            state.facilities.filter(f => state.selectedFacilities.includes(f.facility_id)).map(f => f.facility_name) : undefined,
          providerID: state.selectedProviders.length > 0 ? state.selectedProviders : undefined,
          providers: state.selectedProviders.length > 0 ?
            state.providers.filter(p => state.selectedProviders.includes(p.provider_id)).map(p => p.provider_name) : undefined,
          primaryDx: state.selectedPrimaryDx.length > 0 ? state.selectedPrimaryDx : undefined,
          secondaryDx: state.selectedSecondaryDx.length > 0 ? state.selectedSecondaryDx : undefined,
          primaryInsurance: state.selectedPrimaryInsurance.length > 0 ? state.selectedPrimaryInsurance : undefined,
          secondaryInsurance: state.selectedSecondaryInsurance.length > 0 ? state.selectedSecondaryInsurance : undefined,
          insuranceTypes: state.selectedInsuranceType.length > 0 ? state.selectedInsuranceType : undefined,
          programEligibility: state.selectedProgramEligibility.length > 0 ? state.selectedProgramEligibility : undefined,
          excludeFlag: state.excludeAssignedGroups,
          formattedDate: state.lastVisitDate || undefined
        }
      };

      if (state.isEditMode && state.groupDetails) {
        await groupsService.updateGroup(state.groupDetails.group_id, groupData);
        toast({
          title: "Success",
          description: "Group updated successfully!",
          className: "bg-green-50 border-green-200 text-green-800",
        });
      } else {
        await groupsService.createGroup(groupData);
        toast({
          title: "Success",
          description: "Group created successfully!",
          className: "bg-green-50 border-green-200 text-green-800",
        });
      }

      // Navigate back or reset form
      actions.resetForm();
      
      // Call success callback to navigate back to dashboard and scroll to top
      if (onSuccess) {
        onSuccess();
        // Scroll to top of dashboard after navigation
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    } catch (error) {
      console.error('Error creating/updating group:', error);
      toast({
        title: "Error",
        description: `Failed to ${state.isEditMode ? 'update' : 'create'} group. Please try again.`,
        variant: "destructive"
      });
    } finally {
      actions.setIsLoading(false);
    }
  }, [state, actions, validateForm, checkGroupNameExists, groupsService, toast]);

  /**
   * Update existing group
   */
  const handleUpdateGroup = useCallback(async () => {
    await handleCreateGroup(); // Same logic for update
  }, [handleCreateGroup]);

  /**
   * Create duplicate group
   */
  const handleCreateDuplicateGroup = useCallback(async () => {
    await handleCreateGroup(); // Same logic for duplicate
  }, [handleCreateGroup]);

  /**
   * Handle patient selection
   */
  const handlePatientSelection = useCallback((patientId: number, checked: boolean) => {
    if (checked) {
      actions.setSelectedPatientsForGroup([...state.selectedPatientsForGroup, patientId]);
    } else {
      actions.setSelectedPatientsForGroup(state.selectedPatientsForGroup.filter(id => id !== patientId));
    }
  }, [state.selectedPatientsForGroup, actions]);

  /**
   * Handle select all patients
   */
  const handleSelectAll = useCallback((checked: boolean) => {
    actions.setSelectAll(checked);
    
    if (checked) {
      const allPatientIds = state.patients.map(p => p.patient_clinic_data_id);
      actions.setSelectedPatientsForGroup(allPatientIds);
    } else {
      actions.setSelectedPatientsForGroup([]);
    }
  }, [state.patients, actions]);

  /**
   * Handle EMR patient selection
   */
  const handleEmrPatientSelection = useCallback((patientId: number, checked: boolean) => {
    if (checked) {
      actions.setSelectedEmrPatients([...state.selectedEmrPatients, patientId]);
    } else {
      actions.setSelectedEmrPatients(state.selectedEmrPatients.filter(id => id !== patientId));
      actions.setEmrSelectAll(false);
    }
  }, [state.selectedEmrPatients, actions]);

  /**
   * Handle EMR select all
   */
  const handleEmrSelectAll = useCallback((checked: boolean) => {
    actions.setEmrSelectAll(checked);
    if (checked) {
      actions.setSelectedEmrPatients(state.emrPatients.map(p => p.patient_clinic_data_id));
    } else {
      actions.setSelectedEmrPatients([]);
    }
  }, [state.emrPatients, actions]);

  /**
   * Reset form
   */
  const resetForm = useCallback(() => {
    actions.resetForm();
  }, [actions]);

  return {
    // Data loading operations
    loadInitialData,
    loadGroupDetailsForEdit,
    loadGroupDetailsForDuplicate,
    loadGroupPatientList,
    
    // Group operations
    handleCreateGroup,
    handleUpdateGroup,
    handleCreateDuplicateGroup,
    
    // Validation operations
    checkGroupNameExists,
    validateForm,
    
    // Patient selection operations
    handlePatientSelection,
    handleSelectAll,
    handleEmrPatientSelection,
    handleEmrSelectAll,
    
    // Utility operations
    resetForm,
  };
};